<template>
  <div>
    <q-inner-loading :showing="!activeUser || activeUser.loading" />

    <div
      v-if="activeUser && !activeUser.loading"
      class="column q-gutter-y-xl"
    >
      <q-card
        :class="{
          'q-pb-md': friends.length,
          'bg-responsive-white': !$register || !currentSite
        }"
        :style="{ 'background-color': $register && currentSite ? currentSite.backgroundColor : undefined }"
        flat
      >
        <q-card-section>
          <q-list
            v-if="friends.length"
            :class="{ 'q-mx-lg': $q.screen.gt.xs }"
            separator
          >
            <q-item
              :class="{
                'row items-center justify-between': true,
                'q-px-none': $q.screen.xs
              }"
            >
              <q-item-section>
                <q-item-label
                  caption
                  class="text-uppercase"
                >
                  {{ $t('user.allowPurchasesBy') }}
                </q-item-label>
              </q-item-section>

              <q-item-section>
                <q-item-label
                  caption
                  class="text-uppercase q-mx-auto"
                >
                  {{ user ? user.firstName : $t('person.you') }}
                </q-item-label>
              </q-item-section>

              <q-item-section>
                <q-item-label
                  caption
                  class="text-uppercase q-mx-auto"
                >
                  {{ $tc('person.friend', 1) }}
                </q-item-label>
              </q-item-section>

              <q-item-section
                class="col-auto"
              >
                <q-icon
                  size="sm"
                  name=""
                />
              </q-item-section>
            </q-item>
            <q-slide-item
              v-for="friend in friends"
              :key="friend.id"
              right-color="negative"
              @right="removeFriend(friend)"
            >
              <template
                #right
              >
                <q-icon
                  left
                  name="person_remove_alt_1"
                />
                {{ $t('actions.remove') }}
              </template>
              <q-item
                :class="{
                  'row items-center justify-between': true,
                  'q-px-none': $q.screen.xs
                }"
              >
                <q-item-section>
                  <q-item-label
                    overline
                    class="text-uppercase"
                  >
                    {{ friend.name }}
                  </q-item-label>
                </q-item-section>

                <q-item-section>
                  <q-checkbox
                    class="q-mx-auto"
                    :model-value="friend.isPurchasee"
                    color="primary"
                    dense
                    :disable="!(currentUser.employee && $register) && !friend.isPurchasee"
                    :read-only="!(currentUser.employee && $register) && !friend.isPurchasee"
                    @update:model-value="friend.isPurchasee ? promptRemovePurchasee(friend) : promptAddPurchasee(friend)"
                  >
                    <q-tooltip>
                      {{ user ? $_.startCase(user.name) : 'You' }} can{{ friend.isPurchasee ? '' : 'not' }} make purchases on {{ friend.name }}'s behalf
                    </q-tooltip>
                  </q-checkbox>
                </q-item-section>

                <q-item-section>
                  <q-checkbox
                    class="q-mx-auto"
                    :model-value="friend.isPurchaser"
                    color="primary"
                    dense
                    @update:model-value="promptPurchaser(friend)"
                  >
                    <q-tooltip>
                      {{ friend.name }} can{{ friend.isPurchaser ? '' : 'not' }} make purchases on {{ user ? user.name + '\'s' : 'your' }} behalf
                    </q-tooltip>
                  </q-checkbox>
                </q-item-section>

                <q-item-section
                  class="col-auto"
                >
                  <q-btn
                    icon="person_remove_alt_1"
                    color="grey"
                    flat
                    round
                    dense
                    @click.stop="removeFriend(friend)"
                  >
                    <q-tooltip>
                      Click or swipe to remove {{ friend.name }}
                    </q-tooltip>
                  </q-btn>
                </q-item-section>
              </q-item>
            </q-slide-item>
          </q-list>
          <div
            v-else
            class="text-center"
          >
            {{ $t('user.emptyFriendsList') }}
          </div>
        </q-card-section>
      </q-card>
      <add-friends
        name="ManageAddFriends"
        :user="user"
        :show-manage-friends-button="false"
      />
    </div>
  </div>
</template>

<script>
import { Dialog } from 'quasar'
import AuthorizedPurchaser from 'src/models/authorized-purchaser/AuthorizedPurchaser'
import User from 'src/models/user/User'
import { defineAsyncComponent } from 'vue'

export default {
  name: 'ManageFriends',
  components: {
    AddFriends: defineAsyncComponent(() => import('src/modules/consumer/components/AddFriends'))
  },
  context: ['currentUser'],
  configuration: ['currentSite'],
  props: {
    user: {
      type: User,
      required: false,
      default: null
    }
  },
  data () {
    return {
      friends: []
    }
  },
  computed: {
    activeUser () {
      return this.user || this.currentUser
    }
  },
  watch: {
    'activeUser.loading' () {
      this.computeFriends()
    }
  },
  mounted () {
    if (!this.activeUser) {
      Dialog.create({
        title: 'Login',
        message: 'Login or register to accept friend requests',
        ok: 'Login'
      }).onOk(() => {
        this.$router.push('/login')
      })
    } else {
      const purchaserToken = this.$route.query.authorize
      if (purchaserToken) {
        this.promptToAddPurchaserByToken(purchaserToken)
      }
      this.computeFriends()
    }
  },
  methods: {
    computeFriends () {
      if (!this.activeUser) return
      const friends = _.each(_.sortBy(_.unionBy(this.activeUser.purchasers.data, this.activeUser.purchasees.data, 'id'), ['name']), friend => {
        _.extend(friend, { isPurchaser: false, isPurchasee: false })
        friend.isPurchaser = !!_.find(this.activeUser.purchasers.data, { id: friend.id })
        friend.isPurchasee = !!_.find(this.activeUser.purchasees.data, { id: friend.id })
      })

      this.friends = friends
    },
    promptPurchaser (friend) {
      if (friend.isPurchaser) {
        this.promptRemovePurchaser(friend)
      } else {
        this.promptAddPurchaser(friend)
      }
    },
    promptAddPurchaser (purchaser) {
      Dialog.create({
        title: 'Allow Purchases?',
        message: `Do you want to give permissions for ${purchaser.name} to use ${this.user ? `${this.user.name}'s` : 'your'} membership on ${this.user ? `${this.user.name}'s` : 'your'} behalf?`,
        cancel: true,
        ok: 'Allow Purchases'
      }).onOk(async () => {
        if (await this.addPurchaser(purchaser)) {
          await this.activeUser.fetch(true)
          this.$mh.notify(`Added purchasing permission to ${purchaser.name}`)
        }
      })
    },
    async addPurchaser (purchaser) {
      const authorizedPurchaser = new AuthorizedPurchaser({
        purchaserId: purchaser.id,
        purchaseeId: this.activeUser.id
      })
      const errors = await authorizedPurchaser.addById()

      return !errors.length
    },
    async promptToAddPurchaserByToken (token) {
      const authorizedPurchaser = new AuthorizedPurchaser({
        token: encodeURIComponent(token)
      })

      await authorizedPurchaser.identify()

      Dialog.create({
        title: 'Allow Purchases?',
        message: `Do you want to give permissions for ${authorizedPurchaser.purchaserName} to use ${this.user ? `${this.user.name}'s` : 'your'} membership on ${this.user ? `${this.user.name}'s` : 'your'} behalf?`,
        cancel: true,
        ok: 'Allow Purchases'
      }).onOk(() => {
        this.addPurchaserByToken(token)
      })
    },
    async addPurchaserByToken (token) {
      const authorizedPurchaser = new AuthorizedPurchaser({
        purchaseeId: this.activeUser.id,
        token: encodeURIComponent(token)
      })
      const errors = await authorizedPurchaser.addByToken()

      if (!errors.length) {
        await this.activeUser.fetch(true)
        this.$mh.notify('Accepted friend request')
      }
    },
    promptRemovePurchaser (purchaser) {
      Dialog.create({
        title: 'Rescind Purchase Permission?',
        message: `Do you want to remove permissions for ${purchaser.name} to use ${this.user ? `${this.user.name}'s` : 'your'} membership on ${this.user ? `${this.user.name}'s` : 'your'} behalf?`,
        cancel: true,
        ok: 'Rescind Purchases'
      }).onOk(async () => {
        if (await this.removePurchaser(purchaser)) {
          await this.activeUser.fetch(true)
          this.$mh.notify(`Removed purchasing permission from ${purchaser.name}`)
        }
      })
    },
    async removePurchaser (purchaser) {
      const authorizedPurchaser = new AuthorizedPurchaser({
        purchaserId: purchaser.id,
        purchaseeId: this.activeUser.id
      })
      const errors = await authorizedPurchaser.deleteAuthorizedPurchaser()

      return !errors.length
    },
    promptAddPurchasee (purchasee) {
      Dialog.create({
        title: 'Allow Purchases?',
        message: `Do you want to give permissions for ${this.user.name} to use membership on ${purchasee.name}'s behalf?`,
        cancel: true,
        ok: 'Allow Purchases'
      }).onOk(async () => {
        if (await this.addPurchasee(purchasee)) {
          await this.activeUser.fetch(true)
          this.$mh.notify(`Added purchasing permission to ${this.user.name}`)
        }
      })
    },
    async addPurchasee (purchasee) {
      const authorizedPurchaser = new AuthorizedPurchaser({
        purchaserId: this.activeUser.id,
        purchaseeId: purchasee.id
      })
      const errors = await authorizedPurchaser.addById()

      return !errors.length
    },
    promptRemovePurchasee (purchasee) {
      Dialog.create({
        title: 'Remove Purchase Option?',
        message: `Do you want to remove ${purchasee.name} from ${this.user ? `${this.user.name}'s` : 'your'} list of purchasable member options?`,
        cancel: true,
        ok: 'Remove Purchase'
      }).onOk(async () => {
        if (await this.removePurchasee(purchasee)) {
          await this.activeUser.fetch(true)
          this.$mh.notify(`Removed ${purchasee.name} from purchasable members`)
        }
      })
    },
    async removePurchasee (purchasee) {
      const authorizedPurchaser = new AuthorizedPurchaser({
        purchaserId: this.activeUser.id,
        purchaseeId: purchasee.id
      })
      const errors = await authorizedPurchaser.deleteAuthorizedPurchaser()

      return !errors.length
    },
    async removeFriend (friend) {
      let errors = 0

      if (friend.isPurchaser) {
        if (!await this.removePurchaser(friend)) {
          errors++
        }
      }
      if (friend.isPurchasee) {
        if (!await this.removePurchasee(friend)) {
          errors++
        }
      }

      await this.activeUser.fetch(true)
      if (!errors) {
        this.$mh.notify(`Removed ${friend.name} from friends`)
      } else {
        this.$mh.notify(`Error occurred while removing ${friend.name} from friends`)
      }
    }
  }
}
</script>

<style scoped>
</style>
